import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { fetchReceipt, downloadReceiptPdf } from "@/lib/receipt";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Printer, FileDown, Loader2 } from "lucide-react";

const rs = (v) => "₹" + Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

export default function ReceiptPrintPage() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchReceipt(id).then(setData).catch((e) => setError(formatApiError(e)));
  }, [id]);

  if (error) return <div data-testid="receipt-error" className="p-10 text-center text-rose-600">{error}</div>;
  if (!data) return <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-violet-500" /></div>;
  const { invoice, payments, student, branch } = data;

  return (
    <div className="min-h-screen bg-slate-100 py-8 px-4 print:bg-white print:p-0">
      <div className="max-w-2xl mx-auto flex justify-end gap-2 mb-4 print:hidden">
        <Button data-testid="print-page-btn" onClick={() => window.print()} className="bg-violet-600 hover:bg-violet-700"><Printer size={15} className="mr-1.5" /> Print</Button>
        <Button data-testid="print-page-pdf-btn" variant="outline" onClick={() => downloadReceiptPdf(id)}><FileDown size={15} className="mr-1.5" /> PDF</Button>
      </div>
      <div data-testid="printable-receipt" className="max-w-2xl mx-auto bg-white shadow-lg print:shadow-none rounded-xl overflow-hidden">
        <div className="bg-violet-800 text-white px-8 py-6 flex justify-between items-start">
          <div>
            <div className="font-display text-2xl font-extrabold">SPARKORA EDU</div>
            <div className="text-violet-200 text-sm mt-1">{branch.name} {branch.code && `(${branch.code})`} {branch.location && `• ${branch.location}`}</div>
          </div>
          <div className="text-right">
            <div className="text-xs uppercase tracking-widest text-violet-200">Fee Receipt</div>
            <div data-testid="receipt-number" className="font-mono text-sm mt-1">{invoice.receipt_no}</div>
          </div>
        </div>
        <div className="px-8 py-6 grid grid-cols-2 gap-4 text-sm border-b border-slate-100">
          <Field label="Student" value={student.name} />
          <Field label="Admission No." value={student.admission_number} />
          <Field label="Class" value={`${invoice.class_name} / ${invoice.section}`} />
          <Field label="Period" value={invoice.period} />
          <Field label="Due date" value={invoice.due_date} />
          <Field label="Status" value={invoice.status.toUpperCase()} />
        </div>
        <div className="px-8 py-6">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-slate-200 text-left text-slate-500"><th className="py-2">Fee head</th><th className="py-2 text-right">Amount</th></tr></thead>
            <tbody>
              {invoice.items.map((it, i) => <tr key={i} className="border-b border-slate-50"><td className="py-2">{it.name}</td><td className="py-2 text-right">{rs(it.amount)}</td></tr>)}
            </tbody>
          </table>
          <div className="mt-4 ml-auto w-60 space-y-1 text-sm">
            {invoice.discount > 0 && <Row label="Subtotal" value={rs(invoice.gross)} />}
            {invoice.discount > 0 && <Row label={invoice.discount_note || "Discount"} value={`- ${rs(invoice.discount)}`} cls="text-violet-700" />}
            <Row label="Total" value={rs(invoice.total)} />
            <Row label="Paid" value={rs(invoice.paid)} cls="text-emerald-700" />
            <Row label="Balance due" value={rs(invoice.balance)} cls="text-rose-700 font-bold text-base" />
          </div>
          {payments.length > 0 && (
            <div className="mt-6">
              <div className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-2">Payments received</div>
              <table className="w-full text-xs">
                <tbody>
                  {payments.map((p) => (
                    <tr key={p.id} className="border-b border-slate-50">
                      <td className="py-1.5 font-mono">{p.receipt_no}</td>
                      <td>{new Date(p.received_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</td>
                      <td>{p.mode.toUpperCase()}{p.reference ? ` • ${p.reference}` : ""}</td>
                      <td className="text-right font-semibold">{rs(p.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="text-[11px] text-slate-400 mt-8">This is a computer-generated receipt.</p>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value }) {
  return <div><div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div><div className="text-slate-900 font-medium mt-0.5">{value || "—"}</div></div>;
}

function Row({ label, value, cls = "" }) {
  return <div className={`flex justify-between ${cls}`}><span>{label}</span><span>{value}</span></div>;
}
